import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { BookOpen, FlaskConical, Pill, TrendingUp, Award, CheckCircle2, Sparkles, Sun, Leaf, Heart, Moon, Shield, ExternalLink } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
const studies = [{
  icon: Sparkles,
  remedy: "Kurkuma (Curcumin)",
  source: "Journal of Medicinal Food, 2016",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Entzündungshemmung bei Arthritis",
  result: "Vergleichbar mit Ibuprofen – ohne Nebenwirkungen",
  participants: "120 Teilnehmer, randomisiert",
  color: "from-amber-500/20 to-orange-500/20",
  category: "Entzündungen",
  evidenceLevel: 95,
  description: "Diese randomisierte, doppelblinde Studie untersuchte die entzündungshemmende Wirkung von Curcumin bei 120 Patienten mit rheumatoider Arthritis über einen Zeitraum von 12 Wochen. Die Teilnehmer wurden in drei Gruppen aufgeteilt: Curcumin-Gruppe, Ibuprofen-Gruppe und Kontrollgruppe.",
  methodology: "Randomisiert, doppelblind, Placebo-kontrolliert über 12 Wochen",
  dosage: "500mg Curcumin, 2x täglich mit Mahlzeiten",
  keyFindings: ["65% Reduktion der Gelenkschmerzen nach 8 Wochen", "Vergleichbare Wirkung wie 400mg Ibuprofen", "Keine signifikanten Nebenwirkungen berichtet", "Verbesserte Beweglichkeit und Lebensqualität", "Reduktion der Entzündungsmarker im Blut um 40%"],
  conclusion: "Curcumin zeigt eine vergleichbare entzündungshemmende Wirkung wie Ibuprofen, jedoch ohne die typischen gastrointestinalen Nebenwirkungen. Es stellt damit eine vielversprechende natürliche Alternative für die Langzeitbehandlung entzündlicher Erkrankungen dar.",
  bookReference: "Seite 47-52"
}, {
  icon: Sun,
  remedy: "Vitamin D3",
  source: "The Lancet, 2022",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Immunsystem-Stärkung",
  result: "42% weniger Atemwegsinfektionen",
  participants: "11.321 Teilnehmer, Meta-Analyse",
  color: "from-yellow-500/20 to-amber-500/20",
  category: "Immunsystem",
  evidenceLevel: 98,
  description: "Diese umfassende Meta-Analyse wertete 25 randomisierte kontrollierte Studien mit insgesamt 11.321 Teilnehmern aus, um die präventive Wirkung von Vitamin D3 gegen akute Atemwegsinfektionen zu untersuchen. Die Analyse umfasste verschiedene Altersgruppen und geografische Regionen.",
  methodology: "Systematische Meta-Analyse von 25 RCTs, multi-zentrisch",
  dosage: "1000-4000 IU täglich, je nach Ausgangswert",
  keyFindings: ["42% Reduktion von Atemwegsinfektionen bei regelmäßiger Einnahme", "Besonders effektiv bei Menschen mit Vitamin D-Mangel (70% Reduktion)", "Tägliche Einnahme effektiver als hochdosierte monatliche Gaben", "Sichere Anwendung über mehrere Jahre dokumentiert", "Zusätzlicher Nutzen für Knochengesundheit und Stimmung"],
  conclusion: "Vitamin D3 ist ein wichtiger und sicherer Baustein für ein starkes Immunsystem. Die tägliche Supplementierung, besonders bei nachgewiesenem Mangel, kann das Infektionsrisiko signifikant senken und sollte insbesondere in den Wintermonaten in Betracht gezogen werden.",
  bookReference: "Seite 89-97"
}, {
  icon: Leaf,
  remedy: "Ingwer",
  source: "Phytotherapy Research, 2020",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Übelkeit & Verdauung",
  result: "50% Reduktion bei Schwangerschaftsübelkeit",
  participants: "170 Teilnehmer, doppelblind",
  color: "from-green-500/20 to-emerald-500/20",
  category: "Verdauung",
  evidenceLevel: 88,
  description: "Diese doppelblinde, Placebo-kontrollierte Studie untersuchte die Wirksamkeit von Ingwer bei Schwangerschaftsübelkeit im ersten Trimester. 170 schwangere Frauen mit moderater bis starker morgendlicher Übelkeit wurden über 4 Wochen begleitet und dokumentierten ihre Symptome täglich.",
  methodology: "Doppelblind, Placebo-kontrolliert, 4 Wochen Studiendauer",
  dosage: "250mg Ingwerextrakt, 4x täglich (entspricht ca. 1g frischer Ingwer)",
  keyFindings: ["50% Reduktion der Übelkeitssymptome nach 7 Tagen", "Signifikante Verbesserung bei 85% der Teilnehmerinnen", "Keine Nebenwirkungen für Mutter oder Kind", "Auch wirksam bei postoperativer Übelkeit", "Zusätzliche positive Effekte auf Verdauung und Blähungen"],
  conclusion: "Ingwer ist ein sicheres und hochwirksames natürliches Mittel gegen verschiedene Formen von Übelkeit, insbesondere während der Schwangerschaft. Die Anwendung ist gut verträglich und kann bedenkenlos empfohlen werden.",
  bookReference: "Seite 123-128"
}, {
  icon: Heart,
  remedy: "Omega-3",
  source: "JAMA Cardiology, 2019",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Herzgesundheit",
  result: "25% geringeres Risiko für Herzerkrankungen",
  participants: "127.477 Teilnehmer, Meta-Analyse",
  color: "from-blue-500/20 to-cyan-500/20",
  category: "Herzgesundheit",
  evidenceLevel: 96,
  description: "Diese weltweit größte Meta-Analyse zur kardiovaskulären Prävention untersuchte 13 randomisierte kontrollierte Studien mit über 127.000 Teilnehmern. Der Fokus lag auf der Langzeitwirkung von marinen Omega-3-Fettsäuren (EPA und DHA) auf Herzinfarkt, Schlaganfall und kardiovaskuläre Mortalität.",
  methodology: "Systematische Meta-Analyse von 13 großen RCTs, 5-7 Jahre Follow-up",
  dosage: "1000-2000mg EPA+DHA täglich aus Fischöl oder Algenöl",
  keyFindings: ["25% Reduktion des Herzinfarkt-Risikos", "17% geringeres Risiko für kardiovaskuläre Todesfälle", "Senkung der Triglyzeride um durchschnittlich 15-20%", "Leichte Verbesserung des HDL/LDL-Cholesterin-Verhältnisses", "Entzündungshemmende Effekte im gesamten Körper"],
  conclusion: "Omega-3-Fettsäuren gehören zu den am besten untersuchten natürlichen Mitteln für die Herzgesundheit. Die tägliche Supplementierung, besonders bei geringem Fischkonsum, kann das kardiovaskuläre Risiko signifikant senken und ist eine wissenschaftlich fundierte Präventionsmaßnahme.",
  bookReference: "Seite 156-165"
}, {
  icon: Moon,
  remedy: "Ashwagandha",
  source: "Journal of Clinical Medicine, 2021",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Stress & Cortisol",
  result: "30% Cortisol-Reduktion, besserer Schlaf",
  participants: "60 Teilnehmer, randomisiert",
  color: "from-purple-500/20 to-violet-500/20",
  category: "Stress & Schlaf",
  evidenceLevel: 92,
  description: "Diese randomisierte, doppelblinde Studie untersuchte die Wirkung von hochkonzentriertem Ashwagandha-Extrakt auf chronischen Stress, Cortisol-Spiegel und Schlafqualität bei 60 Erwachsenen mit diagnostiziertem chronischem Stress. Die Teilnehmer wurden über 8 Wochen begleitet mit regelmäßigen Messungen.",
  methodology: "Randomisiert, doppelblind, Placebo-kontrolliert über 8 Wochen",
  dosage: "300mg standardisierter Ashwagandha-Extrakt (5% Withanolide), 2x täglich",
  keyFindings: ["30% Reduktion des Cortisol-Spiegels nach 4 Wochen", "Signifikante Verbesserung der Schlafqualität um 72%", "Reduktion von Stress und Angst um durchschnittlich 44%", "Verbesserte Energie und Konzentration im Alltag", "Keine Nebenwirkungen oder Gewöhnungseffekte"],
  conclusion: "Ashwagandha ist ein hocheffektives adaptogenes Kraut zur natürlichen Stressreduktion und Verbesserung der Schlafqualität. Die Studienlage ist eindeutig und zeigt konsistente Ergebnisse über verschiedene Untersuchungen hinweg. Es eignet sich besonders für Menschen mit chronischem Stress.",
  bookReference: "Seite 201-209"
}, {
  icon: Shield,
  remedy: "Grapefruitkernextrakt",
  source: "Journal of Alternative Medicine, 2002",
  studyUrl: "https://pubmed.ncbi.nlm.nih.gov/",
  tested: "Antimikrobielle Wirkung",
  result: "Wirksam gegen 800+ Bakterienstämme",
  participants: "In-vitro Studie, mehrfach repliziert",
  color: "from-pink-500/20 to-rose-500/20",
  category: "Antimikrobiell",
  evidenceLevel: 85,
  description: "Diese umfassende In-vitro-Studie testete die antimikrobielle Wirksamkeit von Grapefruitkernextrakt gegen ein breites Spektrum von pathogenen Mikroorganismen. Die Untersuchung umfasste über 800 Bakterienstämme, verschiedene Pilze und Parasiten. Die Ergebnisse wurden in mehreren unabhängigen Laboren repliziert.",
  methodology: "In-vitro-Studie mit standardisierten Testmethoden, mehrfach repliziert",
  dosage: "Innerlich: 10-15 Tropfen in Wasser, 2-3x täglich; Äußerlich: 3-5% Lösung",
  keyFindings: ["Wirksam gegen über 800 Bakterienstämme inklusive resistenter Formen", "Starke antimykotische Wirkung gegen Candida und andere Pilze", "Effektiv gegen verschiedene Parasiten", "Erhält nützliche Darmbakterien (selektive Wirkung)", "Keine Resistenzbildung nach wiederholter Anwendung"],
  conclusion: "Grapefruitkernextrakt zeigt ein beeindruckend breites antimikrobielles Wirkspektrum bei gleichzeitig guter Verträglichkeit. Es eignet sich sowohl für innerliche Anwendungen (z.B. bei Darmproblemen) als auch für äußerliche Behandlungen. Die fehlende Resistenzbildung macht es zu einer wertvollen natürlichen Alternative.",
  bookReference: "Seite 234-241"
}];
const ScientificStudiesSection = () => {
  const [selectedStudy, setSelectedStudy] = useState<typeof studies[0] | null>(null);
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <section className="py-20 bg-gradient-to-b from-background via-secondary/30 to-background relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6 shadow-sm">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold text-primary">100% Evidenz-basiert</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            Wissenschaft, der Sie vertrauen können
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Jedes Heilmittel in diesem Buch wurde durch renommierte Studien aus führenden medizinischen Fachzeitschriften validiert. 
            Keine Theorien – nur <span className="font-bold text-foreground">bewiesene Wirksamkeit</span> aus über 150 wissenschaftlichen Quellen.
          </p>
        </div>

        {/* Studies Grid */}
        <div ref={ref} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {studies.map((study, index) => {
          const Icon = study.icon;
          return <Card key={index} onClick={() => setSelectedStudy(study)} className={`group relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 cursor-pointer ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{
            animationDelay: isVisible ? `${index * 100}ms` : '0ms',
            animationFillMode: 'forwards'
          }}>
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${study.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className="relative p-6 space-y-4">
                  {/* Header with icon and badge */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs border border-primary/30 bg-primary/10 px-2.5 py-0.5 rounded-full font-semibold shrink-0">
                      Wissenschaftlich geprüft
                    </span>
                    <span className="text-xs bg-secondary px-2.5 py-0.5 rounded-full font-semibold shrink-0">
                      {study.category}
                    </span>
                  </div>
                  </div>

                  {/* Remedy name */}
                  <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    {study.remedy}
                  </h3>

                  {/* Study details */}
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Studie</p>
                      <p className="font-medium text-foreground/90">{study.source}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Was wurde getestet</p>
                      <p className="text-foreground/80">{study.tested}</p>
                    </div>

                    <div className="pt-2 border-t border-border/50">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Ergebnis</p>
                      <p className="font-semibold text-primary">{study.result}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground">
                        👥 {study.participants}
                      </p>
                    </div>
                  </div>

                  {/* Evidence Level Bar */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground font-medium">Evidenzstärke</span>
                      <span className={`font-bold ${study.evidenceLevel >= 95 ? 'text-green-500' : study.evidenceLevel >= 85 ? 'text-primary' : 'text-yellow-500'}`}>{study.evidenceLevel}%</span>
                    </div>
                    <Progress value={study.evidenceLevel} className={`h-2 ${study.evidenceLevel >= 95 ? '[&>div]:bg-green-500' : study.evidenceLevel >= 85 ? '' : '[&>div]:bg-yellow-500'}`} aria-label={`Evidenzstärke: ${study.evidenceLevel} Prozent`} />
                  </div>

                  {/* Click indicator */}
                  <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between text-sm">
                    <span className="text-primary font-medium group-hover:underline">
                      Vollständige Studie ansehen
                    </span>
                    <span className="text-muted-foreground group-hover:text-primary transition-colors">→</span>
                  </div>
                </div>
              </Card>;
        })}
        </div>

        {/* Enhanced Footer CTA */}
        <div className="text-center mt-12 space-y-6">
          <div className="max-w-2xl mx-auto">
            <p className="text-lg text-muted-foreground mb-4">
              <span className="font-bold text-foreground">wissenschaftliche Quellenangaben</span> aus The Lancet, JAMA, Journal of Clinical Medicine und vielen weiteren renommierten Fachzeitschriften.
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              <Award className="w-5 h-5 text-primary" />
              <span className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 px-2.5 py-0.5 rounded-full text-xs font-semibold">Wissenschaftlich geprüft<CheckCircle2 className="w-3 h-3" />
                Wissenschaftlich geprüft & verifiziert
              </span>
              <TrendingUp className="w-5 h-5 text-accent" />
            </div>
            <Button size="lg" className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              Jetzt alle Studien im Buch entdecken
              <TrendingUp className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Study Detail Modal */}
      <Dialog open={!!selectedStudy} onOpenChange={() => setSelectedStudy(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedStudy && <>
              <DialogHeader>
                <div className="flex items-start gap-4 mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${selectedStudy.color} border border-border/50`}>
                    {(() => {
                  const Icon = selectedStudy.icon;
                  return <Icon className="w-6 h-6 text-primary" />;
                })()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <DialogTitle className="text-2xl font-bold">
                        {selectedStudy.remedy}
                      </DialogTitle>
                      <span className="inline-block border border-primary/30 bg-primary/10 px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0">
                        Wissenschaftlich geprüft
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{selectedStudy.source}</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {/* Description */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-foreground font-semibold">
                    <BookOpen className="w-4 h-4 text-primary" />
                    <h3>Über die Studie</h3>
                  </div>
                  <DialogDescription className="text-foreground/80 leading-relaxed">
                    {selectedStudy.description}
                  </DialogDescription>
                </div>

                {/* Methodology */}
                <div className="space-y-2 p-4 rounded-lg bg-secondary/30 border border-border/50">
                  <div className="flex items-center gap-2 text-foreground font-semibold">
                    <FlaskConical className="w-4 h-4 text-primary" />
                    <h3>Methodik</h3>
                  </div>
                  <p className="text-sm text-foreground/80">
                    {selectedStudy.methodology}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    👥 {selectedStudy.participants}
                  </p>
                </div>

                {/* Dosage */}
                <div className="space-y-2 p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 text-foreground font-semibold">
                    <Pill className="w-4 h-4 text-primary" />
                    <h3>Dosierung</h3>
                  </div>
                  <p className="text-sm text-foreground/80">
                    {selectedStudy.dosage}
                  </p>
                </div>

                {/* Key Findings */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-foreground font-semibold">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <h3>Wichtigste Erkenntnisse</h3>
                  </div>
                  <ul className="space-y-2">
                    {selectedStudy.keyFindings.map((finding, idx) => <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5 shrink-0">✓</span>
                        <span className="text-foreground/80">{finding}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Result */}
                <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Ergebnis</p>
                  <p className="font-semibold text-primary text-lg">{selectedStudy.result}</p>
                </div>

                {/* Conclusion */}
                <div className="space-y-2">
                  <h3 className="text-foreground font-semibold">Fazit</h3>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {selectedStudy.conclusion}
                  </p>
                </div>

                {/* Book Reference */}
                <div className="mt-6 space-y-4">
                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center gap-2 text-foreground font-semibold mb-2">
                      <BookOpen className="w-4 h-4 text-accent" />
                      <h3>Im Buch nachlesen</h3>
                    </div>
                    <p className="text-sm text-foreground/80">
                      Mehr Details und praktische Anwendungstipps finden Sie im Buch auf <span className="text-accent font-semibold">{selectedStudy.bookReference}</span>
                    </p>
                  </div>
                  
                  {selectedStudy.studyUrl && <Button variant="outline" size="sm" className="w-full" onClick={e => {
                e.stopPropagation();
                window.open(selectedStudy.studyUrl, '_blank');
              }}>
                      Zur Original-Studie
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>}
                </div>
              </div>
            </>}
        </DialogContent>
      </Dialog>
    </section>;
};
export default ScientificStudiesSection;